import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'

import { debounce } from 'lodash'
import { v4 as uuidv4 } from 'uuid'
import { useAppDispatch } from '@/state'

import { FormStatusType } from '@/enums'
import routes from '@/routes'
import {
  Bonus,
  BonusResponse,
  GetAllProvidersPayload,
  GetGamesByProviderPayload,
} from '@/state/bonus-engine/models'

import { createBonusEngineThunk } from '@/state/bonus-engine/thunks/create.thunk'
import { getAllGamesByProviderThunk } from '@/state/bonus-engine/thunks/get-all-games.thunk'
import { getProviderCategoriesThunk } from '@/state/bonus-engine/thunks/get-provider-categories.thunk'

import { getProvidersThunk } from '@/state/bonus-engine/thunks/get-providers.thunk'
import { updateBonusEngineThunk } from '@/state/bonus-engine/thunks/update.thunk'
import { getAllBonusesByBundleThunk } from '@/state/bonus-engine/thunks/get-bonuses-by-bundle.thunk'

import { DialogFeedbackType } from '@/state/ui/global/models'
import useDialog from '@/hooks/ui/use-dialog.hook'
import { ApplicationState } from '@/state/root.models'

const useBonusEngineFormActions = () => {
  const { t } = useTranslation()

  const dispatch = useAppDispatch()

  const navigate = useNavigate()

  const { bonusFormMode, currentBonus } = useSelector(
    ({ bonusEngine }: ApplicationState) => bonusEngine,
  )

  const { handleDialogAction, handleCloseDialog } = useDialog()

  const getSideEffect = (response: BonusResponse) => {
    const isCreating =
      bonusFormMode === FormStatusType.CREATE ||
      bonusFormMode === FormStatusType.DUPLICATE

    const success = response._id

    let feedback: DialogFeedbackType = {
      variant: success ? 'success' : 'error',
      formStatus: bonusFormMode,
      item: t('pages.bonusProgram'),
      errorMessage: response.message,
      buttonPrimaryAction: () => {
        navigate(routes.bonus.INDEX)
        handleCloseDialog()
      },
      buttonSecondaryAction: handleCloseDialog,
    }

    if (!success) {
      feedback = {
        ...feedback,
        buttonPrimaryAction: handleCloseDialog,
        buttonSecondaryAction: undefined,
      }
    }

    handleDialogAction({
      open: true,
      id: 'DIALOG_FEEDBACK',
      feedback,
    })

    if (success && isCreating) {
      navigate(`${routes.bonus.UPDATE_BONUS}/${response?._id}`)
    }
  }

  const createBonusProgramAction = async (payload: Bonus) => {
    let dataPayload = payload
    if (bonusFormMode === FormStatusType.DUPLICATE) {
      const copy_id = uuidv4().toString().substring(0, 8)
      if (currentBonus.code === dataPayload.code) {
        dataPayload.code = `${dataPayload.code}_copy_${copy_id}`
      }
      if (currentBonus.name === dataPayload.name) {
        dataPayload.name = `${dataPayload.name}_copy_${copy_id}`
      }
    }
    const resultAction = await dispatch(createBonusEngineThunk(dataPayload))
    if (createBonusEngineThunk.fulfilled.match(resultAction)) {
      getSideEffect(resultAction.payload)
    }
  }

  const updateBonusProgramAction = async (payload: Bonus) => {
    const resultAction = await dispatch(updateBonusEngineThunk(payload))
    if (updateBonusEngineThunk.fulfilled.match(resultAction)) {
      getSideEffect(resultAction.payload)
    }
  }

  const getAllProvidersCategoriesAction = debounce(
    () => {
      dispatch(getProviderCategoriesThunk({}))
    },
    500, // eslint-disable-line
  )

  const getAllProvidersAction = debounce(
    (payload: GetAllProvidersPayload) => {
      dispatch(getProvidersThunk({ ...payload }))
    },
    500, // eslint-disable-line
  )

  const getAllGamesByProviderAction = debounce(
    (payload: GetGamesByProviderPayload) => {
      dispatch(getAllGamesByProviderThunk({ ...payload }))
    },
    500, // eslint-disable-line
  )

  const getAllBonusesByBundleAction = debounce(() => {
    dispatch(getAllBonusesByBundleThunk())
  })

  return {
    createBonusProgramAction,
    getAllProvidersCategoriesAction,
    getAllProvidersAction,
    getAllGamesByProviderAction,
    getAllBonusesByBundleAction,
    updateBonusProgramAction,
  }
}

export default useBonusEngineFormActions
